import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Check, X, Trash2, Star, MessageCircle } from "lucide-react";

const statusColor = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
};

function StarRow({ value }) {
    return (
        <span className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star
                    key={n}
                    className={`h-3.5 w-3.5 ${n <= Math.round(value) ? "fill-yellow-400 stroke-yellow-500" : "stroke-gray-300"}`}
                    strokeWidth={1.5}
                />
            ))}
        </span>
    );
}

export default function ReviewsTab() {
    const [items, setItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [kindFilter, setKindFilter] = useState("all");

    const reload = () => {
        const params = {};
        if (statusFilter !== "all") params.status = statusFilter;
        if (kindFilter !== "all") params.item_kind = kindFilter;
        api.get("/admin/reviews", { params }).then(({ data }) => setItems(data));
    };
    useEffect(() => { reload(); }, [statusFilter, kindFilter]); // eslint-disable-line

    const counts = useMemo(() => {
        const c = { pending: 0, approved: 0, rejected: 0 };
        for (const r of items) c[r.status] = (c[r.status] || 0) + 1;
        return c;
    }, [items]);

    const moderate = async (id, status) => {
        try {
            await api.patch(`/admin/reviews/${id}`, { status });
            toast.success(`Review ${status}`);
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this review permanently?")) return;
        try {
            await api.delete(`/admin/reviews/${id}`);
            toast.success("Review deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div data-testid="reviews-tab" className="space-y-4">
            <div className="flex flex-wrap justify-between items-end gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-semibold flex items-center gap-2">
                        <MessageCircle className="h-5 w-5 text-brand-600" /> Reviews moderation
                    </h2>
                    <p className="text-sm text-ink-700 mt-1">
                        Pending reviews need approval before appearing on the storefront.
                        Showing <strong>{items.length}</strong> {statusFilter === "all" ? "" : statusFilter} review{items.length === 1 ? "" : "s"}.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-40 rounded-full" data-testid="reviews-filter-status">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="pending">Pending {counts.pending ? `(${counts.pending})` : ""}</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                            <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={kindFilter} onValueChange={setKindFilter}>
                        <SelectTrigger className="w-36 rounded-full" data-testid="reviews-filter-kind">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            <SelectItem value="course">Courses</SelectItem>
                            <SelectItem value="product">Products</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {items.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-ink-700">
                    No {statusFilter === "all" ? "" : statusFilter} reviews to show.
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((r) => (
                        <article key={r.id} className="rounded-2xl bg-white border border-gray-100 p-5" data-testid={`admin-review-${r.id}`}>
                            <div className="flex justify-between items-start gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <StarRow value={r.rating} />
                                        <span className={`text-[11px] uppercase tracking-wide rounded-full px-2 py-0.5 ${statusColor[r.status] || "bg-gray-100"}`}>{r.status}</span>
                                        <span className="text-[11px] uppercase tracking-wide rounded-full px-2 py-0.5 bg-brand-50 text-brand-700">{r.item_kind}</span>
                                    </div>
                                    <div className="text-xs text-ink-700 mt-1">
                                        by <strong className="text-ink-900">{r.user_name}</strong> ({r.user_email}) · {String(r.created_at).slice(0, 19).replace("T", " ")}
                                    </div>
                                    <div className="text-xs text-ink-700">on <code className="text-ink-900">{r.item_id}</code></div>
                                </div>
                                <div className="flex gap-2">
                                    {r.status !== "approved" && (
                                        <Button size="sm" onClick={() => moderate(r.id, "approved")} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white" data-testid={`review-approve-${r.id}`}>
                                            <Check className="h-3.5 w-3.5 mr-1" /> Approve
                                        </Button>
                                    )}
                                    {r.status !== "rejected" && (
                                        <Button size="sm" variant="outline" onClick={() => moderate(r.id, "rejected")} className="rounded-full" data-testid={`review-reject-${r.id}`}>
                                            <X className="h-3.5 w-3.5 mr-1" /> Reject
                                        </Button>
                                    )}
                                    <Button size="icon" variant="outline" className="rounded-full h-8 w-8 text-accent-700 hover:bg-accent-50" onClick={() => remove(r.id)} data-testid={`review-delete-${r.id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                            {r.title && <h4 className="font-semibold text-ink-900 mt-3">{r.title}</h4>}
                            {r.body && <p className="mt-1 text-sm text-ink-700 leading-relaxed whitespace-pre-wrap">{r.body}</p>}
                            {r.moderator_note && (
                                <div className="mt-3 rounded-xl bg-paper-alt p-2 text-xs text-ink-700">Moderator note: {r.moderator_note}</div>
                            )}
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
